import { z } from "zod";

// Also export admin types at top level for convenience
export type {
	AdminApp,
	AdminAuditLog,
	AdminDeadLetterEvent,
	AdminEmailDlqRecord,
	AdminPartner,
	AdminPartnerEarning,
	AdminPartnerPayout,
	AdminPurchaseEvent,
	AdminReferralLink,
	AdminUser,
	EmailDlqErrorCategory,
} from "./schemas/admin";
export * as admin from "./schemas/admin";
export * from "./schemas/app";
export * from "./schemas/util";

export const ReferrerCodeSchema = z.string().regex(/^[A-Za-z0-9-]{1,64}$/, {
	error: "Referrer must be 1-64 characters, letters, numbers, or hyphens only",
});

/** Reusable input schema for procedures that take a single `id` param. */
export const ByIdInputSchema = z.object({ id: z.string() });

// =============
// Apps
// =============
export const AppSchema = z.object({
	id: z.string(),
	ownerId: z.string(),
	name: z.string(),
	active: z.boolean().default(true),
	testMode: z.boolean().default(false),
	attributionMode: z.enum(["code", "window"]).default("code"),
	attestationProvider: z
		.enum(["none", "firebase", "apple", "google"])
		.default("none"),
	attestationConfig: z.unknown().nullable().default(null),
	/** Array of IAP provider configs. Null = no providers configured. */
	iapConfigs: z.unknown().nullable().default(null),
	platforms: z.preprocess((v) => {
		return typeof v === "string" ? JSON.parse(v) : v;
	}, z.enum(["ios", "android", "any"]).array()),
	allowedCorsOrigins: z.string().nullable().default(null),
	billingEntityId: z.string().nullable().default(null),
	iosStoreLink: z.url().nullable().default(null),
	androidStoreLink: z.url().nullable().default(null),
	createdAt: z.coerce.date(),
});
export type App = z.infer<typeof AppSchema>;

/** Extended schema for app detail responses with computed fields. */
export const AppDetailSchema = AppSchema.extend({
	computed: z.object({
		hasPayableLinks: z.boolean(),
		isArchivable: z.boolean(),
	}),
});
export type AppDetail = z.infer<typeof AppDetailSchema>;

const iosStoreLinkSchema = z.url().refine(
	(url) => {
		try {
			const parsed = new URL(url);
			return (
				parsed.hostname === "apps.apple.com" ||
				parsed.hostname.endsWith(".apps.apple.com") ||
				parsed.hostname === "itunes.apple.com" ||
				parsed.hostname.endsWith(".itunes.apple.com")
			);
		} catch {
			return false;
		}
	},
	{ message: "Must be a valid Apple App Store URL (apps.apple.com)" },
);

const androidStoreLinkSchema = z.url().refine(
	(url) => {
		try {
			const parsed = new URL(url);
			return (
				parsed.hostname === "play.google.com" ||
				parsed.hostname.endsWith(".play.google.com")
			);
		} catch {
			return false;
		}
	},
	{ message: "Must be a valid Google Play Store URL (play.google.com)" },
);

/** Wraps a schema to treat empty strings as undefined. */
const optionalUrl = <T extends z.ZodTypeAny>(schema: T) =>
	z
		.union([z.literal(""), schema])
		.transform((val) => (val === "" ? undefined : val))
		.optional();

/** Like optionalUrl but also accepts null (for update payloads that clear a value). */
const nullishUrl = <T extends z.ZodTypeAny>(schema: T) =>
	z
		.union([z.literal(""), z.null(), schema])
		.transform((val) => (val === "" ? null : val))
		.nullish();

export const CreateAppSchema = z
	.object({
		name: z.string().min(1),
		platforms: z.enum(["ios", "android", "any"]).array(),
		iosStoreLink: optionalUrl(iosStoreLinkSchema),
		androidStoreLink: optionalUrl(androidStoreLinkSchema),
	})
	.superRefine((data, ctx) => {
		const hasIos = data.platforms.includes("ios");
		const hasAndroid = data.platforms.includes("android");
		const hasAnyLink = !!data.iosStoreLink || !!data.androidStoreLink;

		if ((hasIos || hasAndroid) && !hasAnyLink) {
			ctx.addIssue({
				code: "custom",
				message: "At least one store link is required",
				path: hasIos ? ["iosStoreLink"] : ["androidStoreLink"],
			});
		}
	});

// =============
// =============
// Attestation Config Schemas (for reference/export)
// =============
export const FirebaseAttestationConfigSchema = z.object({
	projectNumber: z.string().min(1),
});

export const AppleAttestationConfigSchema = z.object({
	teamId: z.string().min(1),
	bundleId: z.string().min(1),
});

export const GoogleAttestationConfigSchema = z.object({
	projectNumber: z.string().min(1),
	packageName: z.string().min(1),
});

// =============
// IAP Config Schemas (for reference/export)
// =============
export const RevenueCatIapConfigSchema = z.object({
	webhookAuthorizationSecret: z.string().min(1),
});

export const AppleIapConfigSchema = z.object({
	bundleId: z.string().min(1),
	appAppleId: z.string().min(1),
	issuerId: z.string().min(1),
	keyId: z.string().min(1),
	encodedKey: z.string().min(1),
});

export const GoogleIapConfigSchema = z.object({
	packageName: z.string().min(1),
	pubsubTopic: z.string().min(1),
	serviceAccountJson: z.string().min(1),
});

/** Discriminated union for a single IAP provider config entry. */
export const IapConfigEntrySchema = z.discriminatedUnion("provider", [
	RevenueCatIapConfigSchema.extend({ provider: z.literal("revenuecat") }),
	AppleIapConfigSchema.extend({ provider: z.literal("apple") }),
	GoogleIapConfigSchema.extend({ provider: z.literal("google") }),
]);
export type IapConfigEntry = z.infer<typeof IapConfigEntrySchema>;

// =============
// Update App Schema
// =============
// Flat schema with optional fields - discriminated unions don't work well
// with partial updates. Validation of config structure happens at runtime.
export const UpdateAppSchema = z.object({
	id: z.string(),
	active: z.boolean().optional(),
	testMode: z.boolean().optional(),
	name: z.string().min(1).optional(),
	platforms: z.enum(["ios", "android", "any"]).array().optional(),
	allowedCorsOrigins: z.string().optional(),
	billingEntityId: z
		.string({ message: "A payment method is required." })
		.optional(),
	attributionMode: z.enum(["code", "window"]).optional(),
	// Attestation - optional pair
	attestationProvider: z
		.enum(["none", "firebase", "apple", "google"])
		.optional(),
	attestationConfig: z.unknown().optional(),
	iapConfigs: z.unknown().nullable().optional(),
	// Store links
	iosStoreLink: nullishUrl(iosStoreLinkSchema),
	androidStoreLink: nullishUrl(androidStoreLinkSchema),
});

export type UpdateApp = z.infer<typeof UpdateAppSchema>;

// =============
// Partners
// =============
export const PartnerSchema = z.object({
	id: z.string(),
	appId: z.string().optional(),
	name: z.string(),
	email: z.email(),
	active: z.boolean().optional(),
	stripeAccountId: z.string().nullable(),
	stripeOnboardingStatus: z
		.enum(["creating", "pending", "complete", "restricted"])
		.nullable()
		.optional(),
	stripePayoutsEnabled: z.boolean().nullable().optional(),
	createdAt: z.coerce.date(),
});

export const CreatePartnerSchema = z.object({
	appId: z.string().optional(),
	name: z.string().min(1),
	email: z.email(),
});

export const UpdatePartnerSchema = z.object({
	id: z.string(),
	appId: z.string().optional(), // Required to update active status on appsPartners
	active: z.boolean().optional(),
	// Name/email can only be updated if Stripe is not yet connected
	name: z.string().min(1).optional(),
	email: z.email().optional(),
});

export const PartnerStatisticsSchema = z.object({
	totalEarnings: z.number().int().nonnegative(), // in cents
	paidOut: z.number().int().nonnegative(), // in cents
	pendingPayout: z.number().int().nonnegative(), // in cents
	totalAttributions: z.number().int().nonnegative(),
	currency: z.string().default("usd"),
});

export const PartnerTimeSeriesPointSchema = z.object({
	date: z.string(), // ISO date string YYYY-MM-DD
	earnings: z.number().int().nonnegative(), // in cents
	attributions: z.number().int().nonnegative(),
});

export const PartnerTimeSeriesSchema = z.object({
	points: z.array(PartnerTimeSeriesPointSchema),
});

export const PartnerAppBreakdownItemSchema = z.object({
	appId: z.string(),
	appName: z.string(),
	status: z.boolean(), // active
	attributions: z.number().int().nonnegative(),
	earnings: z.number().int().nonnegative(), // in cents
});

export const PartnerAppBreakdownSchema = z.object({
	items: z.array(PartnerAppBreakdownItemSchema),
});

export const PartnerAttributionSchema = z.object({
	installId: z.string(),
	attributedAt: z.coerce.date().nullable(),
	totalEarnings: z.number().int().nonnegative(), // in cents
	attributionMethod: z.enum(["code", "window"]),
	appName: z.string(),
});

export const ListPartnerAttributionsInputSchema = z.object({
	partnerId: z.string(),
	cursor: z.string().optional(),
	limit: z.number().min(1).max(100).default(20),
});

export const ListPartnerAttributionsOutputSchema = z.object({
	items: z.array(PartnerAttributionSchema),
	nextCursor: z.string().nullable(),
});

// =============
// Referral Links
// =============
export const DeletionStatusSchema = z.object({
	isDeletable: z.boolean(),
	reason: z.string().nullable(),
});

export const ReferralLinkSchema = z.object({
	id: z.string(),
	appId: z.string(),
	partnerId: z.string(),
	slug: ReferrerCodeSchema,
	payoutRateBps: z.number().int().nonnegative(),
	minPayoutCents: z.number().int().nonnegative(),
	payoutDurationDays: z.number().int().positive().nullable(),
	planHint: z.enum(["monthly", "yearly"]).nullable(),
	maxRedemptions: z.number().int().positive().nullable(),
	redemptionCount: z.number(),
	expiresAt: z.coerce.date().nullable(),
	isActive: z.boolean(),
	createdAt: z.coerce.date(),
	/** Partner approval timestamp - null if not yet approved, immutable once set */
	partnerAgreedAt: z.coerce.date().nullable(),
	/** Whether the link can be deleted and the reason if not */
	deletionStatus: DeletionStatusSchema,
});

export const CreateReferralLinkSchema = z.object({
	appId: z.string(),
	partnerId: z.string(),
	slug: ReferrerCodeSchema,
	payoutRateBps: z.number().int().nonnegative(),
	minPayoutCents: z.number().int().min(2500).optional(),
	payoutDurationDays: z.number().int().positive().nullable().optional(),
	planHint: z.enum(["monthly", "yearly"]).nullable().optional(),
	maxRedemptions: z.number().int().positive().nullable(),
	expiresAt: z.coerce.date().nullable(),
});

export const UpdateReferralLinkSchema = z.object({
	id: z.string(),
	slug: ReferrerCodeSchema.optional(),
	payoutRateBps: z.number().int().nonnegative().optional(),
	payoutDurationDays: z.number().int().positive().nullable().optional(), // Can clear duration
	isActive: z.boolean().optional(),
	expiresAt: z.coerce.date().nullable().optional(), // Can clear expiry
	maxRedemptions: z.number().nullable().optional(), // Can clear limit
});

export const ListReferralLinksInputSchema = z.object({
	partnerId: z.string().optional(),
	appId: z.string().optional(),
	cursor: z.string().optional(),
	limit: z.number().min(1).max(100).default(20),
});

export const ListReferralLinksOutputSchema = z.object({
	items: z.array(
		ReferralLinkSchema.extend({
			partnerName: z.string(),
			appName: z.string(),
		}),
	),
	nextCursor: z.string().nullable(),
});

export const ReferralLinkStatisticsSchema = z.object({
	deferCount: z.number().int().nonnegative(),
	claimCount: z.number().int().nonnegative(),
	totalEarnings: z.number().int().nonnegative(), // in cents
	partnerReceived: z.number().int().nonnegative(), // in cents
	currency: z.string().default("usd"),
});

export const ReferralLinkSubscriberSchema = z.object({
	installId: z.string(),
	attributedAt: z.coerce.date().nullable(),
	totalEarnings: z.number().int().nonnegative(), // in cents
	attributionMethod: z.enum(["code", "window"]),
});

export const ListReferralLinkSubscribersInputSchema = z.object({
	referralLinkId: z.string(),
	cursor: z.string().optional(),
	limit: z.number().min(1).max(100).default(20),
});

export const ListReferralLinkSubscribersOutputSchema = z.object({
	items: z.array(ReferralLinkSubscriberSchema),
	nextCursor: z.string().nullable(),
});

export const ReferralLinkTimeSeriesPointSchema = z.object({
	date: z.string(), // ISO date string YYYY-MM-DD
	defers: z.number().int().nonnegative(),
	claims: z.number().int().nonnegative(),
});

export const ReferralLinkTimeSeriesSchema = z.object({
	points: z.array(ReferralLinkTimeSeriesPointSchema),
});

// =============
// Referral Link Partner Approval
// =============

/**
 * Input schema for partner approval of referral link terms.
 * Partners receive an email with a token to approve the link.
 */
export const ApproveReferralLinkInputSchema = z.object({
	linkId: z.string(),
	token: z.string(),
});
export type ApproveReferralLinkInput = z.infer<
	typeof ApproveReferralLinkInputSchema
>;

/**
 * Details shown to partner before approval.
 */
export const ReferralLinkApprovalDetailsSchema = z.object({
	id: z.string(),
	appName: z.string(),
	payoutRateBps: z.number().int().nonnegative(),
	minPayoutCents: z.number().int().nonnegative(),
	payoutDurationDays: z.number().int().positive().nullable(),
	expiresAt: z.coerce.date().nullable(),
});
export type ReferralLinkApprovalDetails = z.infer<
	typeof ReferralLinkApprovalDetailsSchema
>;

/**
 * Output schema for partner approval.
 */
export const ApproveReferralLinkOutputSchema = z.object({
	success: z.boolean(),
	agreedAt: z.coerce.date(),
	link: ReferralLinkApprovalDetailsSchema,
});
export type ApproveReferralLinkOutput = z.infer<
	typeof ApproveReferralLinkOutputSchema
>;

/**
 * Input for fetching link details before approval.
 */
export const GetReferralLinkApprovalDetailsInputSchema = z.object({
	linkId: z.string(),
	token: z.string(),
});

/**
 * Output for fetching link details, includes approval status.
 */
export const GetReferralLinkApprovalDetailsOutputSchema = z.object({
	link: ReferralLinkApprovalDetailsSchema,
	partnerName: z.string(),
	partnerId: z.string(),
	partnerAcceptedTermsAt: z.coerce.date().nullable(),
	alreadyApproved: z.boolean(),
	agreedAt: z.coerce.date().nullable(),
});

// =============
// Referral Link Partner Termination
// =============

export const RequestTerminationInputSchema = z.object({ id: z.string() });
export const RequestTerminationOutputSchema = z.object({
	success: z.boolean(),
});

/** Input for fetching termination details (partner-facing). */
export const GetTerminationDetailsInputSchema = z.object({
	linkId: z.string(),
	token: z.string(),
});

/** Output for fetching termination details. */
export const GetTerminationDetailsOutputSchema = z.object({
	link: ReferralLinkApprovalDetailsSchema,
	partnerName: z.string(),
	appName: z.string(),
	alreadyTerminated: z.boolean(),
});

/** Input for partner confirming termination. */
export const ConfirmTerminationInputSchema = z.object({
	linkId: z.string(),
	token: z.string(),
});

/** Output for partner confirming termination. */
export const ConfirmTerminationOutputSchema = z.object({
	success: z.boolean(),
	deletedAt: z.coerce.date(),
});

/**
 * Input for accepting partner terms of service.
 * Partners can authenticate via either:
 * - stripeConnectToken (from partner signup/self-service flow)
 * - referral link approval token (from referral link approval flow)
 */
export const AcceptPartnerTermsInputSchema = z.discriminatedUnion("type", [
	z.object({
		type: z.literal("stripeConnect"),
		token: z.string(),
	}),
	z.object({
		type: z.literal("referralApproval"),
		linkId: z.string(),
		token: z.string(),
	}),
]);
export type AcceptPartnerTermsInput = z.infer<
	typeof AcceptPartnerTermsInputSchema
>;

/**
 * Output schema for partner terms status check.
 */
export const PartnerTermsStatusOutputSchema = z.object({
	acceptedTermsAt: z.coerce.date().nullable(),
	partnerName: z.string(),
});
export type PartnerTermsStatusOutput = z.infer<
	typeof PartnerTermsStatusOutputSchema
>;

// =============
// Offer Codes
// =============
export const OfferCodeSchema = z.object({
	code: z.string(),
	platform: z.enum(["ios", "android"]),
	subscriptionDuration: z.enum(["monthly", "yearly"]),
	referralLinkId: z.string().nullable(),
	status: z.enum(["available", "reserved", "redeemed"]),
	expiresAt: z.coerce.date(),
	createdAt: z.coerce.date(),
});

export const CreateOfferCodeSchema = z.object({
	codes: z.array(z.string()), // Batch create
	platform: z.enum(["ios", "android"]),
	subscriptionDuration: z.enum(["monthly", "yearly"]),
	referralLinkId: z.string(),
	expiresAt: z.number(),
});

export const UpdateOfferCodeSchema = z.object({
	code: z.string(),
	// Can only disable (change status?) or reassign
	status: z.enum(["available", "reserved", "redeemed"]).optional(),
	referralLinkId: z.string().optional(),
});

// =============
// Offer Code Uploads
// =============

export const UploadOfferCodesInputSchema = z.object({
	referralLinkId: z.string(),
	expiresAt: z
		.date()
		.refine((date) => date > new Date(), {
			message: "Expiry date must be in the future",
		})
		.refine(
			(date) => {
				const oneYearFromNow = new Date();
				oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1);
				return date <= oneYearFromNow;
			},
			{
				message: "Expiry date cannot be more than 1 year from now",
			},
		),

	// CSV content validation happens in the procedure after reading file
	file: z
		.file()
		.min(10)
		.max(5 * 1024 * 1024)
		.mime("text/csv"),
});

export const OfferCodeUploadStatusSchema = z.enum([
	"pending",
	"active",
	"completed",
	"failed",
]);
export type OfferCodeUploadStatus = z.infer<typeof OfferCodeUploadStatusSchema>;

export const OfferCodeUploadSchema = z.object({
	id: z.string(),
	fileName: z.string(),
	platform: z.enum(["ios", "android"]),
	subscriptionDuration: z.enum(["monthly", "yearly"]),
	status: OfferCodeUploadStatusSchema,
	totalCodes: z.number(),
	validCodes: z.number(),
	insertedCodes: z.number(),
	errorMessage: z.string().nullable(),
	createdAt: z.coerce.date(),
});
export type OfferCodeUpload = z.infer<typeof OfferCodeUploadSchema>;

export const ListOfferCodeUploadsInputSchema = z.object({
	referralLinkId: z.string(),
});

export const ListOfferCodeUploadsOutputSchema = z.object({
	items: z.array(OfferCodeUploadSchema),
});

export const OfferCodeStatsSchema = z.object({
	totalCodes: z.number().int().nonnegative(),
	availableCodes: z.number().int().nonnegative(),
	redeemedCodes: z.number().int().nonnegative(),
	isProcessing: z.boolean(),
});
export type OfferCodeStats = z.infer<typeof OfferCodeStatsSchema>;

// =============
// Stripe Connection
// =============
export const StripeConnectionStatusSchema = z.object({
	connected: z.boolean(),
	stripeAccountId: z.string().nullable(),
	detailsSubmitted: z.boolean(),
	payoutsEnabled: z.boolean(),
});

export const CreateStripeConnectionSchema = z.object({
	partnerId: z.string(),
	refreshUrl: z.url(),
	returnUrl: z.url(),
});

export const StripeOnboardingResponseSchema = z.object({
	emailSent: z.boolean(),
	partnerEmail: z.string(),
});

// =============
// Billing Entities (Payment Methods for Apps)
// Each billing entity = 1 Stripe Customer with saved card for off-session charging
// =============
export const BillingEntitySchema = z.object({
	id: z.string(),
	ownerId: z.string(),
	name: z.string(),
	stripeCustomerId: z.string(),
	createdAt: z.coerce.date(),
	updatedAt: z.coerce.date(),
});
export type BillingEntity = z.infer<typeof BillingEntitySchema>;

/**
 * Card info fetched live from Stripe (not stored in DB)
 */
export const PaymentMethodSummarySchema = z.object({
	hasCard: z.boolean(),
	brand: z.string().nullable(), // "visa", "mastercard", etc.
	last4: z.string().nullable(),
	expMonth: z.number().nullable(),
	expYear: z.number().nullable(),
});
export type PaymentMethodSummary = z.infer<typeof PaymentMethodSummarySchema>;

export const BillingEntityWithPaymentMethodSchema = BillingEntitySchema.extend({
	paymentMethod: PaymentMethodSummarySchema,
});
export type BillingEntityWithPaymentMethod = z.infer<
	typeof BillingEntityWithPaymentMethodSchema
>;

export const CreateBillingEntityInputSchema = z.object({
	name: z.string().min(1, "Name is required"),
});

export const CreateBillingEntityOutputSchema = z.object({
	billingEntity: BillingEntitySchema,
	clientSecret: z.string(), // SetupIntent client secret for Payment Element
});

export const RenameBillingEntityInputSchema = z.object({
	id: z.string(),
	name: z.string().min(1, "Name is required"),
});

export const ReplacePaymentMethodInputSchema = z.object({
	id: z.string(),
});

export const ReplacePaymentMethodOutputSchema = z.object({
	clientSecret: z.string(), // SetupIntent client secret for Payment Element
});

export const ConfirmPaymentMethodInputSchema = z.object({
	id: z.string(),
});

export const DeleteBillingEntityInputSchema = z.object({
	id: z.string(),
});

// =============
// Claim
// =============
export const ClaimRequestSchema = z.object({
	appId: z.uuid(),
	installId: z.string(),
	platform: z.enum(["ios", "android"]),
	claimCode: z.string().optional(),
});

export const ClaimResponseSchema = z.object({
	referrer: z.string().nullable(),
	path: z.string().nullable(),
	paywall: z
		.object({
			id: z.string().default("default"),
			offerCode: z.object({
				code: z.string(),
				expiresAt: z.string(), // DateOnly was used, generally means YYYY-MM-DD string or generic date string
				platform: z.enum(["ios", "android"]),
				subscriptionDuration: z.enum(["monthly", "yearly"]),
			}),
			partner: z.string(),
			source: z.enum(["partner"]),
		})
		.nullable(),
	status: z.enum([
		"claimed", // attribution applied now
		"already_claimed", // idempotent replay
		"not_found", // no matching attribution
		"invalid_code", // explicit claim failed
		"limit_exceeded", // plan claim limit reached
	]),
});

export type ClaimResponse = z.infer<typeof ClaimResponseSchema>;
export type ClaimRequestPlatform = z.infer<
	typeof ClaimRequestSchema
>["platform"];

// =============
// Defer
// =============
export const DeferPreflightInputSchema = z.object({
	appId: z.uuid(),
	referrer: ReferrerCodeSchema,
});

export const DeferPreflightOutputSchema = z.object({
	success: z.boolean(),
	claimCode: z.string(),
	expiresAt: z.coerce.date(),
});

export const DeferInputSchema = z.object({
	appId: z.uuid(),
	referrer: ReferrerCodeSchema,
	platform: z.enum(["ios", "android"]),
	path: z
		.string()
		.refine((p) => p.startsWith("/") && !p.includes("://"), {
			message: "Invalid path",
		})
		.optional(),
	claimCode: z.string().optional(),
});

export const DeferOutputSchema = z.object({
	success: z.boolean(),
	expiresAt: z.coerce.date().optional(),
});

// =============
// Notifications
// =============
export const NotificationTypeEnum = z.enum([
	"payout_failed",
	"payout_requires_action",
	"payout_payment_failed",
	"payout_sent",
	"payout_reversed",
	"payout_expired",
	"payout_charge_failed",
	"payout_blocked",
	"partner_restricted",
	"partner_complete",
	"partner_onboarding_started",
	"stripe_account_issue",
	"missing_install_identifier",
	"webhook_dead_lettered",
	"earnings_recorded",
	"refund_processed",
]);

export const NotificationSchema = z.object({
	id: z.string(),
	userId: z.string(),
	type: NotificationTypeEnum,
	title: z.string(),
	message: z.string(),
	metadata: z.record(z.string(), z.unknown()).nullable(),
	read: z.boolean(),
	createdAt: z.coerce.date(),
});

export const ListNotificationsInputSchema = z.object({
	unreadOnly: z.boolean().optional(),
	limit: z.number().min(1).max(100).optional(),
	cursor: z.string().optional(), // ISO timestamp for cursor-based pagination
});

export const ListNotificationsOutputSchema = z.object({
	notifications: z.array(NotificationSchema),
	unreadCount: z.number(),
	nextCursor: z.string().optional(), // ISO timestamp of last item, undefined if no more
});

export const MarkNotificationReadInputSchema = z.object({
	id: z.string(),
});

export const MarkAllNotificationsReadOutputSchema = z.object({
	success: z.boolean(),
});

export const CountNotificationsOutputSchema = z.object({
	count: z.number().int().nonnegative(),
});

// =============
// Partner Earnings
// =============
export const EarningStatusSchema = z.enum([
	"accrued",
	"processing",
	"paid",
	"void",
]);
export type EarningStatus = z.infer<typeof EarningStatusSchema>;

export const EarningSchema = z.object({
	id: z.string(),
	appId: z.string(),
	appName: z.string(),
	partnerId: z.string(),
	partnerName: z.string(),
	installId: z.string(),
	payoutId: z.string().nullable(),
	transactionId: z.string(),
	amountCents: z.number().int(),
	status: EarningStatusSchema,
	periodStart: z.coerce.date().nullable(),
	periodEnd: z.coerce.date().nullable(),
	createdAt: z.coerce.date(),
});
export type Earning = z.infer<typeof EarningSchema>;

// Base filter schema for earnings (shared between list and export)
export const EarningsFilterSchema = z.object({
	appId: z.string().optional(),
	partnerId: z.string().optional(),
	status: EarningStatusSchema.optional(),
	amountMin: z.number().int().nonnegative().optional(),
	amountMax: z.number().int().nonnegative().optional(),
	createdAfter: z.coerce.date().optional(),
	createdBefore: z.coerce.date().optional(),
});
export type EarningsFilter = z.infer<typeof EarningsFilterSchema>;

export const ListEarningsInputSchema = EarningsFilterSchema.extend({
	cursor: z.string().optional(),
	limit: z.number().min(1).max(100).default(20),
});

export const ListEarningsOutputSchema = z.object({
	items: z.array(EarningSchema),
	nextCursor: z.string().nullable(),
	totalCount: z.number().int().nonnegative(),
});

// =============
// Partner Payouts
// =============

/**
 * Internal payout status (matches DB state machine).
 * Exposed to UI so it can show appropriate retry actions.
 */
export const PayoutInternalStatusSchema = z.enum([
	"queued",
	"charging",
	"awaiting_action",
	"charge_failed",
	"charged",
	"transferring",
	"paid",
	"needs_attention",
	"canceled",
]);
export type PayoutInternalStatus = z.infer<typeof PayoutInternalStatusSchema>;

/**
 * Payout status for API filtering:
 * - pending: awaiting processing (internal: queued, processing, transfer_created)
 * - paid: successfully completed
 * - failed: permanently failed
 * - canceled: manually canceled or reversed
 * - needs_attention: requires manual intervention
 */
export const PayoutStatusSchema = z.enum([
	"pending",
	"paid",
	"failed",
	"canceled",
	"needs_attention",
]);
export type PayoutStatus = z.infer<typeof PayoutStatusSchema>;

export const PayoutSchema = z.object({
	id: z.string(),
	partnerId: z.string(),
	partnerName: z.string(),
	amountCents: z.number().int(),
	stripeTransferId: z.string().nullable(),
	status: PayoutStatusSchema,
	internalStatus: PayoutInternalStatusSchema,
	lastError: z.string().nullable(),
	createdAt: z.coerce.date(),
	paidAt: z.coerce.date().nullable(),
});
export type Payout = z.infer<typeof PayoutSchema>;

// Base filter schema for payouts (shared between list and export)
export const PayoutsFilterSchema = z.object({
	partnerId: z.string().optional(),
	status: PayoutStatusSchema.optional(),
	amountMin: z.number().int().nonnegative().optional(),
	amountMax: z.number().int().nonnegative().optional(),
	createdAfter: z.coerce.date().optional(),
	createdBefore: z.coerce.date().optional(),
	paidAfter: z.coerce.date().optional(),
	paidBefore: z.coerce.date().optional(),
});
export type PayoutsFilter = z.infer<typeof PayoutsFilterSchema>;

export const ListPayoutsInputSchema = PayoutsFilterSchema.extend({
	cursor: z.string().optional(),
	limit: z.number().min(1).max(100).default(20),
});

export const ListPayoutsOutputSchema = z.object({
	items: z.array(PayoutSchema),
	nextCursor: z.string().nullable(),
	totalCount: z.number().int().nonnegative(),
});

// =============
// Export Schemas (streaming with event iterator)
// =============
// Export uses the same filters as list, just without pagination
export const ExportEarningsInputSchema = EarningsFilterSchema;
export const ExportPayoutsInputSchema = PayoutsFilterSchema;

// =============
// Payout Retry Schemas
// =============

/** Retry a charge_failed payout by creating a new payout for the accrued earnings */
export const RetryPayoutInputSchema = z.object({
	id: z.string(),
});
export const RetryPayoutOutputSchema = z.object({
	newPayoutId: z.string(),
	message: z.string(),
});

/** Cancel an awaiting_action payout and optionally create a new one */
export const CancelAndRetryPayoutInputSchema = z.object({
	id: z.string(),
	createNewPayout: z.boolean().default(true),
});
export const CancelAndRetryPayoutOutputSchema = z.object({
	canceledPayoutId: z.string(),
	newPayoutId: z.string().nullable(),
	message: z.string(),
});

// =============
// Dashboard
// =============
export const DashboardStatsInputSchema = z.object({
	days: z.number().int().min(1).max(365).default(30),
});

export const DashboardStatsSchema = z.object({
	totalRevenue: z.number().int(), // cents - gross purchase revenue (excludes IAP fees & taxes)
	totalPartnerEarnings: z.number().int(), // cents - partner earnings (their cut)
	totalAttributions: z.number().int(),
	estimatedPayouts: z.number().int(), // cents - accrued earnings awaiting payout
	pendingPayouts: z.number().int(), // cents
	paidPayouts: z.number().int(), // cents
	referralClicks: z.number().int(),
	conversionRate: z.number(), // percentage 0-100
	currency: z.string().default("usd"),
});

export const DashboardTimeSeriesInputSchema = z.object({
	days: z.number().int().min(1).max(365).default(30),
});

export const DashboardTimeSeriesPointSchema = z.object({
	date: z.string(), // YYYY-MM-DD
	value: z.number(),
});

export const DashboardTimeSeriesSchema = z.object({
	points: z.array(DashboardTimeSeriesPointSchema),
});

export const DashboardRevenueByAppItemSchema = z.object({
	appId: z.string(),
	appName: z.string(),
	revenue: z.number().int(), // cents
	percentage: z.number(), // 0-100
});

export const DashboardRevenueByAppSchema = z.object({
	items: z.array(DashboardRevenueByAppItemSchema),
});

export const DashboardLeaderboardInputSchema = z.object({
	days: z.number().int().min(1).max(365).default(30),
	limit: z.number().min(1).max(20).default(10),
});

export const DashboardPartnerLeaderboardItemSchema = z.object({
	partnerId: z.string(),
	partnerName: z.string(),
	earnings: z.number().int(), // cents
});

export const DashboardPartnerLeaderboardSchema = z.object({
	items: z.array(DashboardPartnerLeaderboardItemSchema),
});

export const DashboardPayoutHealthInputSchema = z.object({
	days: z.number().int().min(1).max(365).default(30),
});

export const DashboardPayoutHealthItemSchema = z.object({
	appId: z.string(),
	appName: z.string(),
	estimatedPayouts: z.number().int(), // cents - accrued
	failedPayouts: z.number().int().default(0), // cents - charge_failed + needs_attention + awaiting_action
	stripeBalance: z.number().int().nullable(), // cents - available (null if not connected)
	currency: z.string(),
	status: z.enum(["healthy", "warning", "critical", "unknown"]),
	stripeAccountId: z.string().nullable(),
	stripeAccountLabel: z.string().nullable(),
});

export const DashboardPayoutHealthSchema = z.object({
	items: z.array(DashboardPayoutHealthItemSchema),
	hasRisk: z.boolean(), // true if any item is warning or critical
});

// =============
// App Statistics
// =============
export const AppStatisticsInputSchema = z.object({
	appId: z.string(),
	days: z.number().int().min(1).max(365).default(30),
});

export const AppStatisticsTopLinkSchema = z.object({
	id: z.string(),
	slug: z.string(),
	partnerName: z.string(),
	claimCount: z.number().int().nonnegative(),
	revenue: z.number().int().nonnegative(),
});

export const AppStatisticsSchema = z.object({
	totalInstalls: z.number().int().nonnegative(),
	totalRevenue: z.number().int().nonnegative(), // cents - gross purchase revenue (excludes IAP fees & taxes)
	totalPartnerEarnings: z.number().int().nonnegative(), // cents - partner earnings (their cut)
	pendingPartnerPayouts: z.number().int().nonnegative(), // cents - accrued earnings not yet paid
	topLink: AppStatisticsTopLinkSchema.nullable(),
	currency: z.string().default("usd"),
});

// =============
// Audit Export
// =============
/**
 * Audit export schema for earnings with full FX metadata.
 * Used for regulatory compliance and financial auditing.
 */
export const AuditEarningSchema = z.object({
	transactionId: z.string(),
	appId: z.string(),
	appName: z.string(),
	partnerId: z.string(),
	partnerName: z.string(),
	installId: z.string(),
	eventType: z.string(),
	eventTime: z.coerce.date(),

	// Original amount in source currency
	originalAmountCents: z.number().int(),
	originalCurrency: z.string(),

	// USD-normalized amount
	amountCentsUSD: z.number().int(),

	// FX metadata for audit trail
	exchangeRate: z.string(), // decimal string, e.g. "1.0856"
	exchangeRateDate: z.string(), // YYYY-MM-DD
	exchangeRateSource: z.string(), // "ecb"
	exchangeRateVerificationUrl: z.string(), // Frankfurter API URL for verification

	// Earning status
	earningId: z.string(),
	earningAmountCentsUSD: z.number().int(),
	earningStatus: EarningStatusSchema,
	earningCreatedAt: z.coerce.date(),

	// Payout info (if paid)
	payoutId: z.string().nullable(),
});
export type AuditEarning = z.infer<typeof AuditEarningSchema>;

export const AuditExportInputSchema = z.object({
	appId: z.string().optional(),
	partnerId: z.string().optional(),
	createdAfter: z.coerce.date().optional(),
	createdBefore: z.coerce.date().optional(),
	cursor: z.string().optional(),
	limit: z.number().min(1).max(1000).default(1000),
});
export type AuditExportInput = z.infer<typeof AuditExportInputSchema>;

export const AuditExportOutputSchema = z.object({
	items: z.array(AuditEarningSchema),
	nextCursor: z.string().nullable(),
	totalCount: z.number().int().nonnegative(),
});
export type AuditExportOutput = z.infer<typeof AuditExportOutputSchema>;

// =============
// Onboarding Checklist
// =============

/** Completion status for each onboarding step. */
export const OnboardingChecklistSchema = z.object({
	hasApp: z.boolean(),
	hasPaymentMethod: z.boolean(),
	hasAppWithBilling: z.boolean(),
	hasPartner: z.boolean(),
	hasReferralLink: z.boolean(),
	hasRevenueTracking: z.boolean(),
	/** First app ID for routing to app-specific pages. Null if no apps yet. */
	firstAppId: z.string().nullable(),
});
